import { StyledLoadingPlaceholder, StyledSpinner } from '@tryhackme/thm-ui-components';
import styled, { keyframes } from 'styled-components';

const slowShimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

export const StyledPostRoomCompletionFeedbackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4.6rem;
  width: 100%;
  max-width: 120rem;
  justify-content: center;
  align-items: center;
  padding: 0 2.4rem;
`;

export const StyledPostRoomCompletionFeedbackHeader = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.6rem;
`;

export const StyledPostRoomCompletionFeedbackIcon = styled.div`
  width: 6.4rem;
  height: 6.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const StyledPostRoomCompletionFeedbackTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text.main};
  font-size: 3.2rem;
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  margin: 0;
  line-height: 1.2;
`;

export const StyledPostRoomCompletionFeedbackSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.paragraph};
  font-size: 1.6rem;
  margin: 0;
  text-align: center;
  line-height: 1.4;
`;

export const StyledSectionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 3.2rem;
  align-items: flex-start;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    grid-template-columns: 1fr;
    gap: 2.4rem;
  }
`;

export const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  align-self: flex-start;
`;

export const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
`;

export const StyledSectionIcon = styled.div<{ $variant: 'improvement' | 'good' }>`
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 1.4rem;
    height: 1.4rem;
    color: ${({ theme, $variant }) =>
      $variant === 'improvement' ? theme.colors.error.main : theme.colors.primary.main};
  }
`;

export const StyledSectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.main};
  font-size: 1.8rem;
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  margin: 0;
`;

export const StyledSectionAccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const StyledAccordionItem = styled.div<{ $isOpen?: boolean }>`
  border: ${({ theme }) => `0.1rem solid ${theme.colors.border.light}`};
  border-radius: ${({ theme }) => theme.radius.small};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gradientDark};
`;

export const StyledAccordionHeader = styled.button<{ $isDarkMode?: boolean }>`
  width: 100%;
  background: ${({ theme, $isDarkMode }) => ($isDarkMode ? theme.colors.background.lighter : theme.colors.grey[100])};
  padding: 1.2rem 1.6rem;
  min-height: 4.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;

  &[data-expanded='true'] {
    border: none;

    svg {
      transform: rotate(180deg);
    }
  }

`;

export const StyledAccordionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  text-align: left;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.8rem;
  }
`;

export const StyledAccordionTitle = styled.span<{ $isDarkMode?: boolean }>`
  color: ${({ theme, $isDarkMode }) => ($isDarkMode ? theme.colors.white : theme.colors.black)};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  line-height: 1.4;
`;

export const StyledAccordionChevron = styled.div`
  color: ${({ theme }) => theme.colors.paragraph};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  svg {
    width: 1.6rem;
    height: 1.6rem;
  }

  [data-expanded='true'] & {
    color: ${({ theme }) => theme.colors.info.main};
  }
`;

export const StyledAccordionContent = styled.div<{ $isOpen: boolean }>`
  background: ${({ theme }) => theme.colors.background.main};
  max-height: ${({ $isOpen }) => ($isOpen ? '100rem' : '0')};
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const StyledAccordionBody = styled.div`
  padding: 2.0rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const StyledCommandList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

export const StyledRecommendation = styled.p`
  color: ${({ theme }) => theme.colors.text.main};
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  margin: 0;
  line-height: 1.6;
`;

export const StyledReason = styled.p`
  color: ${({ theme }) => theme.colors.text.main};
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  margin: 0;
  line-height: 1.6;
`;

export const StyledCommandWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
`;

export const StyledCommandLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
`;

export const StyledInlineCommand = styled.code<{ $isDarkMode?: boolean }>`
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  background: ${({ theme, $isDarkMode }) => ($isDarkMode ? theme.colors.black : theme.colors.grey[200])};
  color: ${({ theme, $isDarkMode }) => ($isDarkMode ? theme.colors.primary.main : theme.colors.error.main)};
  padding: 0.4rem 0.8rem;
  border-radius: ${({ theme }) => theme.radius.small};
  border: 0.1rem solid ${({ theme, $isDarkMode }) => ($isDarkMode ? theme.colors.secondary.lighter : theme.colors.grey[300])};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const StyledFlexibleCommandWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  width: 100%;

  ${StyledSectionIcon} {
    flex-shrink: 0;
  }

  ${StyledCommandLabel} {
    flex-shrink: 0;
  }

  ${StyledInlineCommand} {
    flex-shrink: 1;
    min-width: 0;
    margin-left: 0.4rem; /* Additional 0.4rem gap to make total 0.8rem from icon+label */
  }
`;

export const StyledPillContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  flex-wrap: wrap;
`;

export const StyledFooterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const StyledFooterText = styled.p`
  color: ${({ theme }) => theme.colors.grey[400]};
  font-size: ${({ theme }) => theme.fonts.sizes.tiny};
  text-align: center;
  margin: 0;
`;

export const StyledNightOwlPill = styled.div<{ $isDarkMode?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background-color: ${({ theme, $isDarkMode }) =>
    $isDarkMode ? theme.colors.statusTag.purple.background : theme.colors.purple.lighter};
  color: ${({ theme, $isDarkMode }) => ($isDarkMode ? theme.colors.white : theme.colors.black)};
  padding: 0.8rem 1.6rem;
  border-radius: 10rem;
  font-size: 1.4rem;
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};

  svg {
    width: 1.4rem;
    height: 1.4rem;
  }
`;

// Loading skeleton components that match the real accordion styles
export const StyledSkeletonAccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const StyledSkeletonAccordion = styled.div`
  border: ${({ theme }) => `0.1rem solid ${theme.colors.border.light}`};
  border-radius: ${({ theme }) => theme.radius.small};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gradientDark};
`;

export const StyledSkeletonHeader = styled.div<{ $isDarkMode?: boolean }>`
  width: 100%;
  background: ${({ theme, $isDarkMode }) => ($isDarkMode ? theme.colors.background.lighter : theme.colors.grey[100])};
  padding: 1.2rem 1.6rem;
  min-height: 4.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const StyledSkeletonLoading = styled.div<{ width?: string; height?: string }>`
  ${StyledLoadingPlaceholder}
  
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '2rem'};
  
  /* Override the default animation with a slower one */
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.loading.primary},
    ${({ theme }) => theme.colors.loading.secondary},
    ${({ theme }) => theme.colors.loading.primary}
  );
  background-size: 1000px;
  animation: ${slowShimmer} 3s linear infinite;
`;

export const StyledLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.6rem;
`;

export const StyledLoadingText = styled.p`
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  font-style: italic;
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  line-height: 1.9;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.text.main};
`;

export const StyledLoadingSpinner = styled(StyledSpinner)`
  width: 4.2rem;
  height: 4.2rem;
  border-width: 0.32rem;
  color: ${({ theme }) => theme.colors.info.main};
`;
